/**
 * Paint-line stamps (W1-06; R§3.7, F§9.6, DESIGN D24 "approximate paint preview").
 *
 * Phira draws, on every frame with paint(t) > 0, a filled circle of radius paint(t) / W_px * 2 world units at the
 * line origin into a persistent viewport-sized buffer, under the line matrix INCLUDING the line scale (the x0.5
 * scaleX rule applies, so the circle becomes an ellipse), and clears the buffer on every frame with paint(t) <= 0
 * (default -1 before the first event). Colour: colour events or white with alpha max(alpha, 0) * 2.55, saturated
 * at 1. Phira is frame-rate dependent; the editor simulates a fixed 60 Hz grid (k / 60 s) from the last clear up to
 * t, plus the frame at t itself.
 *
 * The grid part is cached per line and extended forward during playback. It is rebuilt when t moved backwards or
 * when any input of the line states may have changed:
 *   - identity keys: the IR of the line and of every ancestor, the tempo map and tempo epoch, the viewport width;
 *   - value keys: maxTime and the package settings the motion half reads (aspectRatio, forceAspectRatio, speedMode,
 *     rpeVersion);
 *   - probes: the last simulated frame, the clearing frame and up to PROBE_STAMPS stamps spread over the cached run
 *     are re-evaluated and compared with what was cached (full stamp: position, radii, colour). They catch inputs
 *     the context does not expose as keys (the engine's own aspect for moveY, for example), so a change is missed
 *     only if it leaves every probed frame bit-identical.
 */

import type { LineId } from '../../model/types';
import type { IrLine } from '../../ir/types';
import type { TempoMap } from '../../time/types';
import type { RGBAf } from '../../util/types';
import type { EngineCompileContext, LineStateRt, PaintStampBatch } from '../types';
import { FRAME_DT, PAINT_ALPHA_FACTOR } from './constants';
import { clamp01 } from './geometry';
import { cacheOf, globalOf } from './store';

type Stamp = PaintStampBatch['stamps'][number];

/** Stamps re-evaluated (besides the last frame and the clearing frame) to validate a cached run. */
const PROBE_STAMPS = 4;

/** Context values (not identities) the line states depend on. */
interface ValueKeys {
  readonly maxTime: number;
  readonly aspectRatio: number;
  readonly forceAspectRatio: boolean;
  readonly speedMode: string;
  readonly rpeVersion: number;
}

/** Raw state of a frame as far as the simulation reads it (paint value, position, scale, alpha, colour). */
type Probe = readonly [number, number, number, number, number, number, number, number, number];

interface PaintCache {
  sig: readonly IrLine[];
  tempo: TempoMap;
  tempoEpoch: number;
  winW: number;
  values: ValueKeys;
  /** Last grid index simulated. */
  k: number;
  clearedAt: number;
  stamps: Stamp[];
  /** Raw state of grid frame k (k >= 0). */
  probe: Probe;
}

/** Stamp of the line state `s` at time `t` (null when paint <= 0 or NaN). */
export function stampOf(s: LineStateRt, t: number, winW: number): Stamp | null {
  const paint = s.paint;
  if (!(paint > 0)) return null;
  const r = (paint / winW) * 2;
  const c = s.color;
  const a = clamp01(Math.max(s.alpha, 0) * PAINT_ALPHA_FACTOR);
  const color: RGBAf = c === null ? [1, 1, 1, a] : [clamp01(c[0]), clamp01(c[1]), clamp01(c[2]), a];
  return { t, x: s.x, y: s.y, rx: r * Math.abs(s.sx), ry: r * Math.abs(s.sy), color };
}

/** IR identities of the line and its ancestors (a change of any of them invalidates the simulation). */
function signature(ctx: EngineCompileContext, line: LineId): IrLine[] {
  const order = ctx.lineOrder();
  const out: IrLine[] = [];
  let cur: LineId | undefined = line;
  const seen = new Set<LineId>();
  while (cur !== undefined && !seen.has(cur)) {
    seen.add(cur);
    const ir = ctx.irLine(cur);
    out.push(ir);
    cur = ir.father >= 0 ? order[ir.father] : undefined;
  }
  return out;
}

function sameSig(a: readonly IrLine[], b: readonly IrLine[]): boolean {
  if (a.length !== b.length) return false;
  for (let k = 0; k < a.length; k++) if (a[k] !== b[k]) return false;
  return true;
}

function valueKeys(ctx: EngineCompileContext): ValueKeys {
  const pkg = ctx.pkg;
  return {
    maxTime: ctx.maxTime,
    aspectRatio: pkg.aspectRatio,
    forceAspectRatio: pkg.forceAspectRatio,
    speedMode: pkg.speedMode,
    rpeVersion: pkg.rpeVersion,
  };
}

function sameValues(a: ValueKeys, b: ValueKeys): boolean {
  return (
    Object.is(a.maxTime, b.maxTime) &&
    Object.is(a.aspectRatio, b.aspectRatio) &&
    a.forceAspectRatio === b.forceAspectRatio &&
    a.speedMode === b.speedMode &&
    Object.is(a.rpeVersion, b.rpeVersion)
  );
}

const NO_PROBE: Probe = [0, 0, 0, 0, 0, 0, 0, 0, 0];

function probeOf(s: LineStateRt): Probe {
  const c = s.color;
  return [s.paint, s.x, s.y, s.sx, s.sy, s.alpha, c?.[0] ?? -1, c?.[1] ?? -1, c?.[2] ?? -1];
}

function sameProbe(a: Probe, b: Probe): boolean {
  for (let k = 0; k < a.length; k++) if (!Object.is(a[k], b[k])) return false;
  return true;
}

function sameStamp(a: Stamp, b: Stamp | null): boolean {
  if (b === null) return false;
  if (!Object.is(a.x, b.x) || !Object.is(a.y, b.y) || !Object.is(a.rx, b.rx) || !Object.is(a.ry, b.ry)) return false;
  for (let k = 0; k < 4; k++) if (!Object.is(a.color[k], b.color[k])) return false;
  return true;
}

/**
 * Re-evaluates the probe frames of a cached run: the last simulated frame, the clearing frame and up to
 * PROBE_STAMPS stamps spread evenly over the run (first and last included).
 */
function probesMatch(ctx: EngineCompileContext, line: LineId, winW: number, cache: PaintCache): boolean {
  if (cache.k < 0) return true;
  if (!sameProbe(cache.probe, probeOf(ctx.lineState(line, cache.k * FRAME_DT)))) return false;
  if (cache.clearedAt >= 0 && !(ctx.lineState(line, cache.clearedAt).paint <= 0)) return false;
  const n = cache.stamps.length;
  if (n === 0) return true;
  const count = Math.min(PROBE_STAMPS, n);
  let last = -1;
  for (let q = 0; q < count; q++) {
    const at = count === 1 ? n - 1 : Math.round((q * (n - 1)) / (count - 1));
    if (at === last) continue;
    last = at;
    const st = cache.stamps[at];
    if (st === undefined) continue;
    if (!sameStamp(st, stampOf(ctx.lineState(line, st.t), st.t, winW))) return false;
  }
  return true;
}

/** Simulates grid frames 0..k backwards from k to the last clear (fresh cache). */
function rebuild(ctx: EngineCompileContext, line: LineId, k: number, winW: number, cache: PaintCache): void {
  const rev: Stamp[] = [];
  let clearedAt = -Infinity;
  for (let j = k; j >= 0; j--) {
    const tj = j * FRAME_DT;
    const s = ctx.lineState(line, tj);
    if (s.paint <= 0) {
      clearedAt = tj;
      break;
    }
    const st = stampOf(s, tj, winW);
    if (st !== null) rev.push(st);
  }
  rev.reverse();
  cache.k = k;
  cache.clearedAt = clearedAt;
  cache.stamps = rev;
  cache.probe = k >= 0 ? probeOf(ctx.lineState(line, k * FRAME_DT)) : NO_PROBE;
}

/** Simulates grid frames cache.k + 1 .. k forwards. */
function extend(ctx: EngineCompileContext, line: LineId, k: number, winW: number, cache: PaintCache): void {
  for (let j = cache.k + 1; j <= k; j++) {
    const tj = j * FRAME_DT;
    const s = ctx.lineState(line, tj);
    if (s.paint <= 0) {
      cache.clearedAt = tj;
      cache.stamps = [];
    } else {
      const st = stampOf(s, tj, winW);
      if (st !== null) cache.stamps.push(st);
    }
    if (j === k) cache.probe = probeOf(s);
  }
  cache.k = k;
}

const EMPTY: PaintStampBatch = { clearedAt: -Infinity, stamps: [] };

/** EngineNotesModule.paintStamps. */
export function paintStamps(ctx: EngineCompileContext, line: LineId, t: number, winW: number): PaintStampBatch {
  const now = ctx.lineState(line, t);
  if (Number.isNaN(now.paint) || !(t >= 0) || !(winW > 0)) return EMPTY;
  const nowStamp = stampOf(now, t, winW);
  const nowClears = now.paint <= 0;
  const k = Math.floor(t / FRAME_DT + 1e-9);
  const onGrid = Math.abs(t - k * FRAME_DT) <= 1e-9;
  const g = globalOf(ctx);
  const caches = cacheOf(g, 'paint', () => new Map<LineId, PaintCache>());
  const sig = signature(ctx, line);
  const values = valueKeys(ctx);
  let cache = caches.get(line);
  const valid =
    cache !== undefined &&
    cache.tempo === ctx.tempo &&
    cache.tempoEpoch === g.tempoEpoch &&
    cache.winW === winW &&
    sameValues(cache.values, values) &&
    sameSig(cache.sig, sig) &&
    cache.k <= k &&
    probesMatch(ctx, line, winW, cache);
  if (cache === undefined || !valid) {
    cache = {
      sig,
      tempo: ctx.tempo,
      tempoEpoch: g.tempoEpoch,
      winW,
      values,
      k: -1,
      clearedAt: -Infinity,
      stamps: [],
      probe: NO_PROBE,
    };
    caches.set(line, cache);
    rebuild(ctx, line, k, winW, cache);
  } else if (cache.k < k) extend(ctx, line, k, winW, cache);
  if (onGrid) return { clearedAt: cache.clearedAt, stamps: cache.stamps };
  if (nowClears) return { clearedAt: t, stamps: [] };
  return { clearedAt: cache.clearedAt, stamps: nowStamp === null ? cache.stamps : [...cache.stamps, nowStamp] };
}
